/**
 * SpoofIO API Client
 *
 * Provides typed methods for interacting with the Admin API.
 * Uses Bearer token auth via Clerk JWT.
 */

import type {
  Organization,
  Canary,
  AuthorizedDomain,
  Detection,
  OrganizationRecipient,
  CanaryRecipient,
  PaginatedResponse,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationWithStats,
  CreateCanaryRequest,
  UpdateCanaryRequest,
  CanaryResponse,
  CanaryWithStats,
  AddAuthorizedDomainRequest,
  DetectionQueryParams,
  DetectionResponse,
  AddRecipientRequest,
  DashboardStats,
  UserProfileResponse,
  UpdateUserProfileRequest,
  BillingStatusResponse,
  CreateCheckoutSessionResponse,
  CreatePortalSessionResponse,
  InvoiceRecipientResponse,
  UsageSummaryResponse,
  PlatformDashboardStats,
  AdminOrganizationResponse,
  AdminOrganizationDetailResponse,
  AdminUserResponse,
  DetectionTrendPoint,
  AlertDeliveryStats,
  EmailProviderSetting,
  UpdateEmailProviderSettingRequest,
  ComponentCategory,
  DetailEntry,
  ComponentHealth,
  InfrastructureHealthResponse,
  EmailProviderName,
} from '@spoofio/shared/types'

// ============================================================================
// Infrastructure Types
// ============================================================================

/**
 * The Infrastructure Health contract is defined once in `@spoofio/shared/types` and
 * consumed by both the admin-api handler that produces it and this client. Re-exported
 * here so UI modules keep importing their API types from a single module.
 */
export type {
  ComponentCategory,
  DetailEntry,
  ComponentHealth,
  InfrastructureHealthResponse,
  EmailProviderName,
}

export type TestEmailResponse = {
  success: boolean
  messageId?: string
}

export type ResetDataResponse = {
  success: boolean
  deleted: {
    alerts: number
    detections: number
    dedupKeys: number
  }
}

// ============================================================================
// Email Test Harness Types
// ============================================================================

export type EmailTemplateCategory = 'transactional' | 'alert' | 'billing'

export type EmailTemplateDataSource =
  | 'none'
  | 'organization'
  | 'organization+subscription'
  | 'detection'
  | 'user'

/** The record kinds the preview/test-send endpoints accept for body hydration. */
export type EmailTemplateRecordType = 'organization' | 'detection' | 'user'

export type EmailTemplateMeta = {
  id: string
  name: string
  description: string
  category: EmailTemplateCategory
  dataSource: EmailTemplateDataSource
}

export type EmailTemplatePreviewRequest = {
  recordType?: EmailTemplateRecordType
  recordId?: string
}

export type EmailTemplatePreviewResponse = {
  subject: string
  html: string
}

export type EmailTemplateTestSendRequest = {
  email: string
  provider?: EmailProviderName
  recordType?: EmailTemplateRecordType
  recordId?: string
}

export type EmailTemplateTestSendResponse = {
  success: boolean
  messageId?: string | null
}

// ============================================================================
// Billing Types
// ============================================================================

export type StripeInvoice = {
  id: string
  number: string | null
  status: string
  amountDue: number
  amountPaid: number
  currency: string
  created: number
  periodStart: number
  periodEnd: number
  invoicePdf: string
  hostedInvoiceUrl: string
}

export type InvoiceListResponse = {
  invoices: StripeInvoice[]
  hasMore: boolean
}

export type UsageResponse = {
  data: UsageSummaryResponse[]
  total: number
}

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type ApiErrorBody = {
  error?: string
  code?: string
  details?: Record<string, string[]>
}

// ============================================================================
// Allowlist Types
// ============================================================================

export type AllowlistEntryResponse = {
  id: string
  signalType: 'referrer' | 'user_agent' | 'ip_range'
  pattern: string
  description: string
  isActive: boolean
  createdAt: string
}

export type CreateAllowlistEntryRequest = {
  signalType: 'referrer' | 'user_agent' | 'ip_range'
  pattern: string
  description: string
}

export type UpdateAllowlistEntryRequest = {
  signalType?: 'referrer' | 'user_agent' | 'ip_range'
  pattern?: string
  description?: string
  isActive?: boolean
}

// ============================================================================
// Impersonation Types
// ============================================================================

export type ImpersonationResponse = {
  token: string
  organizationId: string
  organizationName: string
  expiresAt: string
}

export type ImpersonationStatusResponse = {
  active: boolean
  organizationId?: string
  organizationName?: string
  expiresAt?: string
}

// ============================================================================
// Notification Types
// ============================================================================

export type NotificationResponse = {
  id: string
  type: string
  title: string
  message: string
  severity: 'info' | 'warning' | 'critical'
  organizationId: string | null
  metadata: Record<string, unknown> | null
  isRead: boolean
  createdAt: string
}

/**
 * Token provider function — set by the app after Clerk initializes.
 * Returns a Bearer token for authenticated requests.
 */
let tokenProvider: (() => Promise<string | null>) | null = null
let impersonationToken: string | null = null

export function setTokenProvider(provider: () => Promise<string | null>): void {
  tokenProvider = provider
}

export function setImpersonationToken(token: string): void {
  impersonationToken = token
}

export function clearImpersonationToken(): void {
  impersonationToken = null
}

class ApiClient {
  readonly baseUrl: string = (import.meta.env.VITE_API_URL as string | undefined) ?? ''
  private readonly apiPath = '/api/v1'

  private async request<T>(
    path: string,
    options?: RequestInit
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string> | undefined),
    }

    // Add Bearer token if available
    if (tokenProvider) {
      const token = await tokenProvider()
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
    }

    // Add impersonation header if active
    if (impersonationToken) {
      headers['X-Impersonate-Token'] = impersonationToken
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    })

    if (!response.ok) {
      let errorData: ApiErrorBody
      try {
        errorData = (await response.json()) as ApiErrorBody
      } catch {
        errorData = { error: 'Unknown error', code: 'UNKNOWN' }
      }
      throw new ApiError(
        response.status,
        errorData.code ?? 'UNKNOWN',
        errorData.error ?? 'An error occurred',
        errorData.details
      )
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json() as Promise<T>
  }

  // ============================================================================
  // Dashboard
  // ============================================================================

  async getDashboardStats(orgId?: string): Promise<DashboardStats> {
    const path = orgId
      ? `${this.apiPath}/organizations/${orgId}/dashboard`
      : `${this.apiPath}/dashboard`
    const response = await this.request<{ data: DashboardStats }>(path)
    return response.data
  }

  // ============================================================================
  // Organizations
  // ============================================================================

  async listOrganizations(): Promise<Organization[]> {
    const response = await this.request<PaginatedResponse<Organization>>(`${this.apiPath}/organizations`)
    return response.data
  }

  async getOrganization(id: string): Promise<OrganizationWithStats> {
    const response = await this.request<{ data: OrganizationWithStats }>(`${this.apiPath}/organizations/${id}`)
    return response.data
  }

  async createOrganization(
    data: CreateOrganizationRequest
  ): Promise<Organization & { apiKey: string }> {
    const response = await this.request<{ data: Organization & { apiKey: string } }>(
      `${this.apiPath}/organizations`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<Organization> {
    const response = await this.request<{ data: Organization }>(
      `${this.apiPath}/organizations/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async deleteOrganization(id: string): Promise<void> {
    await this.request(`${this.apiPath}/organizations/${id}`, { method: 'DELETE' })
  }

  async regenerateApiKey(id: string): Promise<{ apiKey: string }> {
    const response = await this.request<{ data: { apiKey: string } }>(
      `${this.apiPath}/organizations/${id}/api-key`,
      { method: 'POST' }
    )
    return response.data
  }

  // ============================================================================
  // Canaries
  // ============================================================================

  async listCanaries(orgId: string): Promise<CanaryWithStats[]> {
    const response = await this.request<PaginatedResponse<CanaryWithStats>>(
      `${this.apiPath}/organizations/${orgId}/canaries`
    )
    return response.data
  }

  async getCanary(id: string): Promise<CanaryResponse> {
    const response = await this.request<{ data: CanaryResponse }>(`${this.apiPath}/canaries/${id}`)
    return response.data
  }

  async createCanary(
    orgId: string,
    data: CreateCanaryRequest
  ): Promise<CanaryResponse> {
    const response = await this.request<{ data: CanaryResponse }>(
      `${this.apiPath}/organizations/${orgId}/canaries`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async updateCanary(id: string, data: UpdateCanaryRequest): Promise<Canary> {
    const response = await this.request<{ data: Canary }>(`${this.apiPath}/canaries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    return response.data
  }

  async deleteCanary(id: string): Promise<void> {
    await this.request(`${this.apiPath}/canaries/${id}`, { method: 'DELETE' })
  }

  async verifyCanary(canaryId: string): Promise<{
    id: string
    canaryId: string
    confidenceScore: number
    isTest: boolean
  }> {
    const response = await this.request<{ data: {
      id: string
      canaryId: string
      confidenceScore: number
      isTest: boolean
    } }>(
      `${this.apiPath}/canaries/${canaryId}/verify`,
      { method: 'POST' }
    )
    return response.data
  }

  // ============================================================================
  // Authorized Domains
  // ============================================================================

  async addAuthorizedDomain(
    canaryId: string,
    data: AddAuthorizedDomainRequest
  ): Promise<AuthorizedDomain> {
    const response = await this.request<{ data: AuthorizedDomain }>(
      `${this.apiPath}/canaries/${canaryId}/authorized-domains`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async updateAuthorizedDomain(
    id: string,
    data: { fqdn: string }
  ): Promise<AuthorizedDomain> {
    const response = await this.request<{ data: AuthorizedDomain }>(
      `${this.apiPath}/authorized-domains/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async removeAuthorizedDomain(id: string): Promise<void> {
    await this.request(`${this.apiPath}/authorized-domains/${id}`, { method: 'DELETE' })
  }

  // ============================================================================
  // Detections
  // ============================================================================

  async listDetections(
    params: DetectionQueryParams & { organizationId?: string }
  ): Promise<PaginatedResponse<DetectionResponse>> {
    const searchParams = new URLSearchParams()

    if (params.canaryId) searchParams.set('canaryId', params.canaryId)
    if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom)
    if (params.dateTo) searchParams.set('dateTo', params.dateTo)
    if (params.minConfidence !== undefined) {
      searchParams.set('minConfidence', params.minConfidence.toString())
    }
    if (params.maxConfidence !== undefined) {
      searchParams.set('maxConfidence', params.maxConfidence.toString())
    }
    if (params.fakeFqdn) searchParams.set('fakeFqdn', params.fakeFqdn)
    if (params.limit) searchParams.set('limit', params.limit.toString())
    if (params.offset) searchParams.set('offset', params.offset.toString())
    if (params.sortBy) searchParams.set('sortBy', params.sortBy)
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)

    const queryString = searchParams.toString()
    const basePath = params.organizationId
      ? `${this.apiPath}/organizations/${params.organizationId}/detections`
      : params.canaryId
        ? `${this.apiPath}/canaries/${params.canaryId}/detections`
        : `${this.apiPath}/detections`

    const path = queryString ? `${basePath}?${queryString}` : basePath
    return this.request<PaginatedResponse<DetectionResponse>>(path)
  }

  async getDetection(id: string): Promise<DetectionResponse> {
    const response = await this.request<{ data: DetectionResponse }>(`${this.apiPath}/detections/${id}`)
    return response.data
  }

  async dismissDetection(id: string): Promise<Detection> {
    const response = await this.request<{ data: Detection }>(
      `${this.apiPath}/detections/${id}/dismiss`,
      { method: 'POST' }
    )
    return response.data
  }

  async fetchScreenshot(detectionId: string): Promise<Blob> {
    const headers: Record<string, string> = {}
    if (tokenProvider) {
      const token = await tokenProvider()
      if (token) headers.Authorization = `Bearer ${token}`
    }
    const response = await fetch(
      `${this.baseUrl}${this.apiPath}/screenshots/${detectionId}`,
      { headers }
    )
    if (!response.ok) throw new Error('Screenshot not available')
    return response.blob()
  }

  // ============================================================================
  // Recipients
  // ============================================================================

  async listOrganizationRecipients(orgId: string): Promise<OrganizationRecipient[]> {
    const response = await this.request<PaginatedResponse<OrganizationRecipient>>(
      `${this.apiPath}/organizations/${orgId}/recipients`
    )
    return response.data
  }

  async addOrganizationRecipient(
    orgId: string,
    data: AddRecipientRequest
  ): Promise<OrganizationRecipient> {
    const response = await this.request<{ data: OrganizationRecipient }>(
      `${this.apiPath}/organizations/${orgId}/recipients`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async removeOrganizationRecipient(id: string): Promise<void> {
    await this.request(`${this.apiPath}/organization-recipients/${id}`, { method: 'DELETE' })
  }

  async listCanaryRecipients(canaryId: string): Promise<CanaryRecipient[]> {
    const response = await this.request<PaginatedResponse<CanaryRecipient>>(
      `${this.apiPath}/canaries/${canaryId}/recipients`
    )
    return response.data
  }

  async addCanaryRecipient(
    canaryId: string,
    data: AddRecipientRequest
  ): Promise<CanaryRecipient> {
    const response = await this.request<{ data: CanaryRecipient }>(
      `${this.apiPath}/canaries/${canaryId}/recipients`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async removeCanaryRecipient(id: string): Promise<void> {
    await this.request(`${this.apiPath}/canary-recipients/${id}`, { method: 'DELETE' })
  }

  // ============================================================================
  // Infrastructure
  // ============================================================================

  async getInfrastructureHealth(): Promise<InfrastructureHealthResponse> {
    const response = await this.request<{ data: InfrastructureHealthResponse }>(
      `${this.apiPath}/infrastructure/health`
    )
    return response.data
  }

  async sendTestEmail(data: {
    email: string
    provider?: EmailProviderName
  }): Promise<TestEmailResponse> {
    const response = await this.request<{ data: TestEmailResponse }>(
      `${this.apiPath}/infrastructure/test-email`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async resetData(): Promise<ResetDataResponse> {
    const response = await this.request<{ data: ResetDataResponse }>(
      `${this.apiPath}/infrastructure/reset-data`,
      { method: 'POST' }
    )
    return response.data
  }

  // ============================================================================
  // Email Test Harness (Platform Admin)
  // ============================================================================

  async listEmailTemplates(): Promise<EmailTemplateMeta[]> {
    const response = await this.request<{ data: EmailTemplateMeta[] }>(
      `${this.apiPath}/admin/email-templates`
    )
    return response.data
  }

  async previewEmailTemplate(
    id: string,
    body: EmailTemplatePreviewRequest
  ): Promise<EmailTemplatePreviewResponse> {
    const response = await this.request<{ data: EmailTemplatePreviewResponse }>(
      `${this.apiPath}/admin/email-templates/${id}/preview`,
      { method: 'POST', body: JSON.stringify(body) }
    )
    return response.data
  }

  async sendEmailTemplateTest(
    id: string,
    body: EmailTemplateTestSendRequest
  ): Promise<EmailTemplateTestSendResponse> {
    const response = await this.request<{ data: EmailTemplateTestSendResponse }>(
      `${this.apiPath}/admin/email-templates/${id}/test-send`,
      { method: 'POST', body: JSON.stringify(body) }
    )
    return response.data
  }

  // ============================================================================
  // System Settings — Email Provider (Platform Admin)
  // ============================================================================

  async getEmailProviderSetting(): Promise<EmailProviderSetting> {
    const response = await this.request<{ data: EmailProviderSetting }>(
      `${this.apiPath}/system-settings/email-provider`
    )
    return response.data
  }

  async updateEmailProviderSetting(
    provider: EmailProviderName
  ): Promise<EmailProviderSetting> {
    const body: UpdateEmailProviderSettingRequest = { provider }
    const response = await this.request<{ data: EmailProviderSetting }>(
      `${this.apiPath}/system-settings/email-provider`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      }
    )
    return response.data
  }

  // ============================================================================
  // User Profile
  // ============================================================================

  async getUserProfile(): Promise<UserProfileResponse> {
    const response = await this.request<{ data: UserProfileResponse }>(
      `${this.apiPath}/users/profile`
    )
    return response.data
  }

  async updateUserProfile(data: UpdateUserProfileRequest): Promise<UserProfileResponse> {
    const response = await this.request<{ data: UserProfileResponse }>(
      `${this.apiPath}/users/profile`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async completeOnboarding(): Promise<UserProfileResponse> {
    const response = await this.request<{ data: UserProfileResponse }>(
      `${this.apiPath}/users/complete-onboarding`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    )
    return response.data
  }

  // ============================================================================
  // Billing
  // ============================================================================

  async getBillingStatus(orgId: string): Promise<BillingStatusResponse> {
    const response = await this.request<{ data: BillingStatusResponse }>(
      `${this.apiPath}/organizations/${orgId}/billing/status`
    )
    return response.data
  }

  async createCheckoutSession(orgId: string, data: {
    planType: string
    successUrl: string
    cancelUrl: string
  }): Promise<CreateCheckoutSessionResponse> {
    const response = await this.request<{ data: CreateCheckoutSessionResponse }>(
      `${this.apiPath}/organizations/${orgId}/billing/checkout`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async createPortalSession(orgId: string, data: {
    returnUrl: string
    flow?: string
  }): Promise<CreatePortalSessionResponse> {
    const response = await this.request<{ data: CreatePortalSessionResponse }>(
      `${this.apiPath}/organizations/${orgId}/billing/portal`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async addDomainAddon(orgId: string): Promise<{ message: string }> {
    const response = await this.request<{ data: { message: string } }>(
      `${this.apiPath}/organizations/${orgId}/billing/add-domain`,
      { method: 'POST' }
    )
    return response.data
  }

  async removeDomainAddon(orgId: string): Promise<{ message: string }> {
    const response = await this.request<{ data: { message: string } }>(
      `${this.apiPath}/organizations/${orgId}/billing/remove-domain`,
      { method: 'POST' }
    )
    return response.data
  }

  async addCanaryAddon(orgId: string): Promise<{ message: string }> {
    const response = await this.request<{ data: { message: string } }>(
      `${this.apiPath}/organizations/${orgId}/billing/add-canary`,
      { method: 'POST' }
    )
    return response.data
  }

  async removeCanaryAddon(orgId: string): Promise<{ message: string }> {
    const response = await this.request<{ data: { message: string } }>(
      `${this.apiPath}/organizations/${orgId}/billing/remove-canary`,
      { method: 'POST' }
    )
    return response.data
  }

  async enableFfiecCanary(orgId: string, canaryId: string): Promise<{ success: boolean }> {
    const response = await this.request<{ data: { success: boolean } }>(
      `${this.apiPath}/organizations/${orgId}/billing/enable-ffiec-canary`,
      { method: 'POST', body: JSON.stringify({ canaryId }) }
    )
    return response.data
  }

  async disableFfiecCanary(orgId: string, canaryId: string): Promise<{ success: boolean }> {
    const response = await this.request<{ data: { success: boolean } }>(
      `${this.apiPath}/organizations/${orgId}/billing/disable-ffiec-canary`,
      { method: 'POST', body: JSON.stringify({ canaryId }) }
    )
    return response.data
  }

  async cancelSubscription(orgId: string): Promise<{ message: string }> {
    const response = await this.request<{ data: { message: string } }>(
      `${this.apiPath}/organizations/${orgId}/billing/cancel`,
      { method: 'POST' }
    )
    return response.data
  }

  async reactivateSubscription(orgId: string, data: {
    planType: string
    successUrl: string
    cancelUrl: string
  }): Promise<CreateCheckoutSessionResponse> {
    const response = await this.request<{ data: CreateCheckoutSessionResponse }>(
      `${this.apiPath}/organizations/${orgId}/billing/reactivate`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async listInvoices(orgId: string, params?: {
    limit?: number
    startingAfter?: string
  }): Promise<InvoiceListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.startingAfter) searchParams.set('starting_after', params.startingAfter)
    const query = searchParams.toString()
    const path = query
      ? `${this.apiPath}/organizations/${orgId}/billing/invoices?${query}`
      : `${this.apiPath}/organizations/${orgId}/billing/invoices`
    const response = await this.request<{ data: InvoiceListResponse }>(path)
    return response.data
  }

  async listInvoiceRecipients(orgId: string): Promise<InvoiceRecipientResponse[]> {
    const response = await this.request<{ data: InvoiceRecipientResponse[] }>(
      `${this.apiPath}/organizations/${orgId}/invoice-recipients`
    )
    return response.data
  }

  async addInvoiceRecipient(orgId: string, data: { email: string }): Promise<InvoiceRecipientResponse> {
    const response = await this.request<{ data: InvoiceRecipientResponse }>(
      `${this.apiPath}/organizations/${orgId}/invoice-recipients`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return response.data
  }

  async removeInvoiceRecipient(orgId: string, id: string): Promise<void> {
    await this.request(`${this.apiPath}/organizations/${orgId}/invoice-recipients/${id}`, {
      method: 'DELETE',
    })
  }

  async getUsage(orgId: string, params?: {
    canaryId?: string
    dateFrom?: string
    dateTo?: string
    limit?: number
    offset?: number
  }): Promise<UsageResponse> {
    const query = new URLSearchParams()
    if (params?.canaryId) query.set('canaryId', params.canaryId)
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom)
    if (params?.dateTo) query.set('dateTo', params.dateTo)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))

    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/organizations/${orgId}/usage?${queryStr}`
      : `${this.apiPath}/organizations/${orgId}/usage`
    const response = await this.request<{ data: UsageResponse }>(path)
    return response.data
  }
  // ============================================================================
  // Platform Admin — Dashboard
  // ============================================================================

  async getAdminDashboardStats(): Promise<PlatformDashboardStats> {
    const response = await this.request<{ data: PlatformDashboardStats }>(
      `${this.apiPath}/admin/dashboard`
    )
    return response.data
  }

  async getDetectionTrends(params?: {
    orgId?: string
    dateFrom?: string
    dateTo?: string
    groupBy?: 'day' | 'hour'
  }): Promise<DetectionTrendPoint[]> {
    const query = new URLSearchParams()
    if (params?.orgId) query.set('orgId', params.orgId)
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom)
    if (params?.dateTo) query.set('dateTo', params.dateTo)
    if (params?.groupBy) query.set('groupBy', params.groupBy)
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/dashboard/detection-trends?${queryStr}`
      : `${this.apiPath}/admin/dashboard/detection-trends`
    const response = await this.request<{ data: DetectionTrendPoint[] }>(path)
    return response.data
  }

  async getAlertDeliveryStats(params?: {
    dateFrom?: string
    dateTo?: string
  }): Promise<AlertDeliveryStats> {
    const query = new URLSearchParams()
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom)
    if (params?.dateTo) query.set('dateTo', params.dateTo)
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/dashboard/alert-stats?${queryStr}`
      : `${this.apiPath}/admin/dashboard/alert-stats`
    const response = await this.request<{ data: AlertDeliveryStats }>(path)
    return response.data
  }

  // ============================================================================
  // Platform Admin — Organizations
  // ============================================================================

  async adminListOrganizations(params?: {
    search?: string
    status?: string
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<AdminOrganizationResponse>> {
    const query = new URLSearchParams()
    if (params?.search) query.set('search', params.search)
    if (params?.status) query.set('status', params.status)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/organizations?${queryStr}`
      : `${this.apiPath}/admin/organizations`
    return this.request<PaginatedResponse<AdminOrganizationResponse>>(path)
  }

  async adminCreateOrganization(
    data: { name: string; slug: string; ownerEmail?: string; activateImmediately?: boolean }
  ): Promise<AdminOrganizationResponse & { apiKey: string }> {
    const response = await this.request<{ data: AdminOrganizationResponse & { apiKey: string } }>(
      `${this.apiPath}/admin/organizations`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminDeleteOrganization(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/organizations/${id}`, { method: 'DELETE' })
  }

  async adminGetOrganization(id: string): Promise<AdminOrganizationDetailResponse> {
    const response = await this.request<{ data: AdminOrganizationDetailResponse }>(
      `${this.apiPath}/admin/organizations/${id}`
    )
    return response.data
  }

  async adminUpdateOrganization(
    id: string,
    data: { name?: string; slug?: string; graylogEndpoint?: string; graylogApiKey?: string }
  ): Promise<AdminOrganizationDetailResponse> {
    const response = await this.request<{ data: AdminOrganizationDetailResponse }>(
      `${this.apiPath}/admin/organizations/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminExtendTrial(id: string, days: number): Promise<void> {
    await this.request(`${this.apiPath}/admin/organizations/${id}/extend-trial`, {
      method: 'POST',
      body: JSON.stringify({ days }),
    })
  }

  async adminSuspendOrganization(id: string, reason?: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/organizations/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  async adminReactivateOrganization(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/organizations/${id}/reactivate`, {
      method: 'POST',
    })
  }

  async adminCancelOrganization(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/organizations/${id}/cancel`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // Platform Admin — Users
  // ============================================================================

  async adminListUsers(params?: {
    search?: string
    orgId?: string
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<AdminUserResponse>> {
    const query = new URLSearchParams()
    if (params?.search) query.set('search', params.search)
    if (params?.orgId) query.set('orgId', params.orgId)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/users?${queryStr}`
      : `${this.apiPath}/admin/users`
    return this.request<PaginatedResponse<AdminUserResponse>>(path)
  }

  async adminListOrgUsers(orgId: string, params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<AdminUserResponse>> {
    const query = new URLSearchParams()
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/organizations/${orgId}/users?${queryStr}`
      : `${this.apiPath}/admin/organizations/${orgId}/users`
    return this.request<PaginatedResponse<AdminUserResponse>>(path)
  }

  async adminCreateUser(
    data: { email: string; firstName?: string; lastName?: string }
  ): Promise<AdminUserResponse> {
    const response = await this.request<{ data: AdminUserResponse }>(
      `${this.apiPath}/admin/users`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminDeleteUser(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/users/${id}`, { method: 'DELETE' })
  }

  async adminUpdateUser(
    id: string,
    data: { firstName?: string; lastName?: string; phone?: string; mobilePhone?: string }
  ): Promise<AdminUserResponse> {
    const response = await this.request<{ data: AdminUserResponse }>(
      `${this.apiPath}/admin/users/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminPasswordReset(userId: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/users/${userId}/password-reset`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // Platform Admin — Members
  // ============================================================================

  async adminAddMember(
    orgId: string,
    data: { email: string; role: string }
  ): Promise<{ id: string; userId: string; email: string; role: string; createdAt: string }> {
    const response = await this.request<{
      data: { id: string; userId: string; email: string; role: string; createdAt: string }
    }>(
      `${this.apiPath}/admin/organizations/${orgId}/members`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminRemoveMember(orgId: string, userId: string): Promise<void> {
    await this.request(
      `${this.apiPath}/admin/organizations/${orgId}/members/${userId}`,
      { method: 'DELETE' }
    )
  }

  async adminUpdateMemberRole(
    orgId: string,
    userId: string,
    data: { role: string }
  ): Promise<{ id: string; userId: string; role: string }> {
    const response = await this.request<{
      data: { id: string; userId: string; role: string }
    }>(
      `${this.apiPath}/admin/organizations/${orgId}/members/${userId}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    )
    return response.data
  }

  // ============================================================================
  // Platform Admin — Canaries
  // ============================================================================

  async adminListCanaries(params?: {
    search?: string
    orgId?: string
    isActive?: boolean
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<CanaryWithStats & { organizationName: string }>> {
    const query = new URLSearchParams()
    if (params?.search) query.set('search', params.search)
    if (params?.orgId) query.set('orgId', params.orgId)
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/canaries?${queryStr}`
      : `${this.apiPath}/admin/canaries`
    return this.request<PaginatedResponse<CanaryWithStats & { organizationName: string }>>(path)
  }

  async adminListOrgCanaries(orgId: string, params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<CanaryWithStats>> {
    const query = new URLSearchParams()
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/organizations/${orgId}/canaries?${queryStr}`
      : `${this.apiPath}/admin/organizations/${orgId}/canaries`
    return this.request<PaginatedResponse<CanaryWithStats>>(path)
  }

  async adminCreateCanary(data: {
    organizationId: string
    name: string
    description?: string
    authorizedDomains: string[]
    screenshotEnabled?: boolean
    confidenceThreshold?: number
  }): Promise<CanaryResponse> {
    const response = await this.request<{ data: CanaryResponse }>(
      `${this.apiPath}/admin/canaries`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminUpdateCanary(
    id: string,
    data: {
      name?: string
      description?: string
      screenshotEnabled?: boolean
      confidenceThreshold?: number
      isActive?: boolean
    }
  ): Promise<CanaryResponse> {
    const response = await this.request<{ data: CanaryResponse }>(
      `${this.apiPath}/admin/canaries/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminDeleteCanary(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/canaries/${id}`, { method: 'DELETE' })
  }

  async adminAddAuthorizedDomain(
    canaryId: string,
    fqdn: string
  ): Promise<AuthorizedDomain> {
    const response = await this.request<{ data: AuthorizedDomain }>(
      `${this.apiPath}/admin/canaries/${canaryId}/authorized-domains`,
      { method: 'POST', body: JSON.stringify({ fqdn }) }
    )
    return response.data
  }

  async adminRemoveAuthorizedDomain(domainId: string): Promise<void> {
    await this.request(
      `${this.apiPath}/admin/authorized-domains/${domainId}`,
      { method: 'DELETE' }
    )
  }

  // ============================================================================
  // Platform Admin — Detections
  // ============================================================================

  async adminListDetections(params?: {
    orgId?: string
    canaryId?: string
    dateFrom?: string
    dateTo?: string
    minConfidence?: number
    maxConfidence?: number
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<DetectionResponse>> {
    const query = new URLSearchParams()
    if (params?.orgId) query.set('orgId', params.orgId)
    if (params?.canaryId) query.set('canaryId', params.canaryId)
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom)
    if (params?.dateTo) query.set('dateTo', params.dateTo)
    if (params?.minConfidence !== undefined) query.set('minConfidence', String(params.minConfidence))
    if (params?.maxConfidence !== undefined) query.set('maxConfidence', String(params.maxConfidence))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/detections?${queryStr}`
      : `${this.apiPath}/admin/detections`
    return this.request<PaginatedResponse<DetectionResponse>>(path)
  }

  async adminListOrgDetections(orgId: string, params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<DetectionResponse>> {
    const query = new URLSearchParams()
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/organizations/${orgId}/detections?${queryStr}`
      : `${this.apiPath}/admin/organizations/${orgId}/detections`
    return this.request<PaginatedResponse<DetectionResponse>>(path)
  }

  async adminResendAlert(detectionId: string, recipients?: string[]): Promise<void> {
    await this.request(`${this.apiPath}/admin/detections/${detectionId}/resend-alert`, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    })
  }

  // ============================================================================
  // Platform Admin — Global Allowlist
  // ============================================================================

  async adminListAllowlistEntries(params?: {
    signalType?: string
    isActive?: boolean
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<AllowlistEntryResponse>> {
    const query = new URLSearchParams()
    if (params?.signalType) query.set('signalType', params.signalType)
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/allowlist?${queryStr}`
      : `${this.apiPath}/admin/allowlist`
    return this.request<PaginatedResponse<AllowlistEntryResponse>>(path)
  }

  async adminGetAllowlistEntry(id: string): Promise<AllowlistEntryResponse> {
    const response = await this.request<{ data: AllowlistEntryResponse }>(
      `${this.apiPath}/admin/allowlist/${id}`
    )
    return response.data
  }

  async adminCreateAllowlistEntry(data: CreateAllowlistEntryRequest): Promise<AllowlistEntryResponse> {
    const response = await this.request<{ data: AllowlistEntryResponse }>(
      `${this.apiPath}/admin/allowlist`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminUpdateAllowlistEntry(
    id: string,
    data: UpdateAllowlistEntryRequest
  ): Promise<AllowlistEntryResponse> {
    const response = await this.request<{ data: AllowlistEntryResponse }>(
      `${this.apiPath}/admin/allowlist/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    )
    return response.data
  }

  async adminDeleteAllowlistEntry(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/allowlist/${id}`, { method: 'DELETE' })
  }

  // ============================================================================
  // Platform Admin — Impersonation
  // ============================================================================

  async adminStartImpersonation(organizationId: string): Promise<ImpersonationResponse> {
    const response = await this.request<{ data: ImpersonationResponse }>(
      `${this.apiPath}/admin/impersonate`,
      { method: 'POST', body: JSON.stringify({ organizationId }) }
    )
    return response.data
  }

  async adminEndImpersonation(): Promise<void> {
    await this.request(`${this.apiPath}/admin/impersonate/end`, { method: 'POST' })
  }

  async adminGetImpersonationStatus(): Promise<ImpersonationStatusResponse> {
    const response = await this.request<{ data: ImpersonationStatusResponse }>(
      `${this.apiPath}/admin/impersonate/status`
    )
    return response.data
  }

  // ============================================================================
  // Platform Admin — Notifications
  // ============================================================================

  async adminListNotifications(params?: {
    type?: string
    severity?: string
    isRead?: boolean
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<NotificationResponse>> {
    const query = new URLSearchParams()
    if (params?.type) query.set('type', params.type)
    if (params?.severity) query.set('severity', params.severity)
    if (params?.isRead !== undefined) query.set('isRead', String(params.isRead))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/notifications?${queryStr}`
      : `${this.apiPath}/admin/notifications`
    return this.request<PaginatedResponse<NotificationResponse>>(path)
  }

  async adminGetUnreadCount(): Promise<{ count: number }> {
    const response = await this.request<{ data: { count: number } }>(
      `${this.apiPath}/admin/notifications/unread-count`
    )
    return response.data
  }

  async adminMarkNotificationRead(id: string): Promise<void> {
    await this.request(`${this.apiPath}/admin/notifications/${id}/read`, {
      method: 'PATCH',
    })
  }

  async adminMarkAllNotificationsRead(): Promise<void> {
    await this.request(`${this.apiPath}/admin/notifications/mark-all-read`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // Platform Admin — Audit Logs
  // ============================================================================

  async adminListAuditLogs(params?: {
    orgId?: string
    action?: string
    userEmail?: string
    dateFrom?: string
    dateTo?: string
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<{
    id: string
    action: string
    resourceType: string
    resourceId: string
    userEmail: string
    organizationId: string | null
    metadata: Record<string, unknown> | null
    createdAt: string
  }>> {
    const query = new URLSearchParams()
    if (params?.orgId) query.set('orgId', params.orgId)
    if (params?.action) query.set('action', params.action)
    if (params?.userEmail) query.set('userEmail', params.userEmail)
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom)
    if (params?.dateTo) query.set('dateTo', params.dateTo)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/audit-logs?${queryStr}`
      : `${this.apiPath}/admin/audit-logs`
    return this.request<PaginatedResponse<{
      id: string
      action: string
      resourceType: string
      resourceId: string
      userEmail: string
      organizationId: string | null
      metadata: Record<string, unknown> | null
      createdAt: string
    }>>(path)
  }

  // ============================================================================
  // Platform Admin — Integration Errors
  // ============================================================================

  async adminListIntegrationErrors(params?: {
    service?: string
    worker?: string
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<{
    id: string
    service: string
    operation: string
    httpStatus: number | null
    errorCode: string | null
    errorMessage: string
    context: Record<string, unknown> | null
    worker: string
    createdAt: string
  }>> {
    const query = new URLSearchParams()
    if (params?.service) query.set('service', params.service)
    if (params?.worker) query.set('worker', params.worker)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const queryStr = query.toString()
    const path = queryStr
      ? `${this.apiPath}/admin/integration-errors?${queryStr}`
      : `${this.apiPath}/admin/integration-errors`
    return this.request<PaginatedResponse<{
      id: string
      service: string
      operation: string
      httpStatus: number | null
      errorCode: string | null
      errorMessage: string
      context: Record<string, unknown> | null
      worker: string
      createdAt: string
    }>>(path)
  }
}

export const api = new ApiClient()
export { ApiError }
